import { ContentAttribute } from "JS/Models/Attributes";
import {
    AttributeType,
    incrementPlaylistSnackCount,
    resetAllAttributesState,
    resetAudioSelectedSpeakers,
    resetVideoSelectedSpeakers,
    setAudioSelectedSpeakers,
    setAudioSpeakers,
    setVideoSelectedSpeakers,
    setVideoSpeakers,
} from "JS/Redux/Attributes";
import { useAppDispatch, useAppSelector } from "JS/Redux/Store";
import { AttributesService } from "JS/Services/Attributes";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";

export const service = new AttributesService();

export const useAttributes = (contentType: string, skip?: boolean) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [attributes, setAttributes] = useState<ContentAttribute[]>([]);
    const {
        setAudioSpeakers,
        setVideoSpeakers,
        setAudioSelectedSpeakers,
        setVideoSelectedSpeakers,
        audioSelectedSpeakers,
        videoSelectedSpeakers,
    } = useGlobalAttributes();

    const { enqueueSnackbar } = useSnackbar();

    const fetchAttributesByContentType = useCallback((contentType: string) => {
        setLoading(true);
        return service
            .getAttributesByContentType(contentType)
            .then(async (response) => {
                setAttributes(response.data);
                if (contentType === AttributeType.AUDIO) {
                    setAudioSpeakers(response.data);
                    if (audioSelectedSpeakers?.length > 0) {
                        const audSelectedSpeakers =
                            audioSelectedSpeakers?.filter((x) =>
                                response.data?.some((z) =>
                                    z.attributeValues.includes(x),
                                ),
                            ) || [];
                        setAudioSelectedSpeakers(audSelectedSpeakers);
                    }
                }
                if (contentType === AttributeType.VIDEO) {
                    setVideoSpeakers(response.data);
                    if (videoSelectedSpeakers?.length > 0) {
                        const vidSelectedSpeakers =
                            videoSelectedSpeakers?.filter((x) =>
                                response.data?.some((z) =>
                                    z.attributeValues.includes(x),
                                ),
                            ) || [];
                        setVideoSelectedSpeakers(vidSelectedSpeakers);
                    }
                }
                return response;
            })
            .catch((err) => {
                enqueueSnackbar(err.message, {
                    variant: "error",
                });
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!skip) fetchAttributesByContentType(contentType);
    }, [skip]);

    return {
        attributes,
        fetchAttributesByContentType,
        loading,
    };
};

export const useGlobalAttributes = () => {
    const audioMeta = useAppSelector((state) => state.attributes)?.audio;
    const audioUniqueSpeakers = audioMeta?.speakers || [];
    const audioFetchStatus = audioMeta?.isAttributeFetched;
    const audioSelectedSpeakers = audioMeta?.selectedSpeakers;

    const videoMeta = useAppSelector((state) => state.attributes)?.video;
    const videoUniqueSpeakers = videoMeta?.speakers || [];
    const videoFetchStatus = videoMeta?.isAttributeFetched;
    const videoSelectedSpeakers = videoMeta?.selectedSpeakers;

    const dispatch = useAppDispatch();

    return {
        audioFetchStatus,
        audioUniqueSpeakers,
        audioSelectedSpeakers,
        videoUniqueSpeakers,
        videoFetchStatus,
        videoSelectedSpeakers,
        showPlaylistSnackbar: (audioMeta.playlistSnackMsgCount || 0) < 3,
        incrementPlaylistSnackCount: () =>
            dispatch(incrementPlaylistSnackCount()),
        setAudioSpeakers: (attributes: ContentAttribute[]) =>
            dispatch(setAudioSpeakers(attributes)),
        setVideoSpeakers: (attributes: ContentAttribute[]) =>
            dispatch(setVideoSpeakers(attributes)),
        setAudioSelectedSpeakers: (speakers: string[]) =>
            dispatch(setAudioSelectedSpeakers(speakers)),
        setVideoSelectedSpeakers: (speakers: string[]) =>
            dispatch(setVideoSelectedSpeakers(speakers)),
        resetAudioSelectedSpeakers: () =>
            dispatch(resetAudioSelectedSpeakers()),
        resetVideoSelectedSpeakers: () =>
            dispatch(resetVideoSelectedSpeakers()),
        resetAllAttributes: () => dispatch(resetAllAttributesState()),
    };
};
